import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { css } from "styled-components";


interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: CartProductPartNumbersStyles;
}

type Props = OwnProps;

export interface CartProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    partContainer?: InjectableCss;
}

export const cartProductPartNumbersStyles: CartProductPartNumbersStyles = {
    container: {
        gap: 0,
        css: css`
        div {
            display: block !important;
        }
        `,
    },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            color: #002554;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;            
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                margin-bottom: .31250625rem;
            }
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;
            margin-left: .5em;
            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;            
            }
        `,
    },
    erpNumberGridItem: {
        css: css`
            display: block;
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;        
            }
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            color: #002554;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                margin-right: .625rem;
                color: #002554;
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                margin-bottom: .31250625rem;             
            }
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;
            margin-left: .5em;
            @media screen and (max-width: 1399px) {
                margin-bottom: .31250625rem;            
            }
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            color: #002554;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;

            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-right: .625rem;
                color: #002554;
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                margin-bottom: .31250625rem;              
            }
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            line-height: 1.25rem;
            margin-left: .5em;
            @media screen and (max-width: 1399px) {
                display: inline-block;
                margin-bottom: .31250625rem;            
            }
        `,
    },
    partContainer: {
        css: css`
            display: block;
        `,
    }
};

const CartProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(cartProductPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem} {...styles.partContainer}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFG #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText}>{manufacturerItem}</Typography>
                </GridItem>
            )}
            <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>}
                {!showLabel && (
                    <VisuallyHidden>
                        <Typography {...styles.erpNumberLabelText}>{translate("Part #")}</Typography>
                    </VisuallyHidden>
                )}
                <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                    {productNumber}
                </Typography>
            </GridItem>
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem} {...styles.partContainer}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText}>{customerProductNumber}</Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default CartProductPartNumbers;
